// Parse typed wikilinks out of a card's body. Renders to small chips below
// the body in CardRow. Keep aligned with substrate's WikilinkParser regex.
//
//   →supports     [[c-7]]   → typed edge
//   →contradicts  [[c-9]]
//   →depends-on   [[c-5]]
//   →derived-from [[c-1]]
//
// ASCII arrow `->` also accepted for editor convenience.

export type EdgeType =
  | 'supports'
  | 'contradicts'
  | 'depends-on'
  | 'derived-from'
  | string;

export interface Edge {
  type: EdgeType;
  targetId: string;
}

const TYPED = /(?:→|->)\s*([a-z][a-z0-9-]*)\s*\[\[([A-Za-z0-9_-]+)\]\]/g;
const ANY_REF = /\[\[([A-Za-z0-9_-]+)\]\]/g;

export function parseEdges(body: string): Edge[] {
  const out: Edge[] = [];
  for (const m of body.matchAll(TYPED)) {
    out.push({ type: m[1], targetId: m[2] });
  }
  return out;
}

/**
 * Distinct ids referenced by `[[…]]` in the body (typed edges + bare
 * mentions). CardRow subtracts the project's known-id set from this to
 * surface broken wikilinks below the body.
 */
export function parseAllRefs(body: string): string[] {
  const seen = new Set<string>();
  for (const m of body.matchAll(ANY_REF)) {
    seen.add(m[1]);
  }
  return [...seen];
}

/** Unicode glyph hint for an edge — paired with type label in the chip. */
export function edgeArrow(type: EdgeType): string {
  switch (type) {
    case 'supports':       return '↑';   // upward — strengthens
    case 'contradicts':    return '↯';   // lightning — clash
    case 'depends-on':     return '→';   // dependency
    case 'derived-from':   return '↓';   // downward from source
    default:               return '·';
  }
}

export function edgeColor(type: EdgeType): string {
  switch (type) {
    case 'supports':     return 'var(--blue)';
    case 'contradicts':  return 'var(--red)';
    case 'depends-on':   return 'var(--ink-2)';
    case 'derived-from': return 'var(--ink-3)';
    default:             return 'var(--ink-3)';
  }
}
